/**
 * Temaly's icon geometry as plain data, so the app (ui/Icon.tsx) and the static landing pages
 * (seo/icons.ts) draw the same shapes. The rules for the set are at the top of ui/Icon.tsx and in
 * docs/DESIGN.md section 6.
 *
 * Each icon on a 24 grid:
 * - `s` stroke paths (the outline weight),
 * - `f` the silhouette, tinted in the active ("fill") weight,
 * - `a` the marker accent, filled with `var(--icon-accent, currentColor)` (tool icons only),
 * - `d` solid detail, always filled (dots, play triangle).
 */
export type IconDef = { s?: string; f?: string; a?: string; d?: string }

const n = (v: number) => String(Math.round(v * 100) / 100)

/** Circle as a path. Circles run a touch larger than squares (r 9 = 18 vs 16) to look the same size. */
const c = (cx: number, cy: number, r: number) =>
	`M${n(cx - r)} ${n(cy)}a${n(r)} ${n(r)} 0 1 0 ${n(2 * r)} 0a${n(r)} ${n(r)} 0 1 0 ${n(-2 * r)} 0Z`

/** Rounded rectangle. Radius 2 on a 24 grid is the 8/12/16 radius family at icon scale. */
const rr = (x: number, y: number, w: number, h: number, r = 2) =>
	`M${n(x + r)} ${n(y)}h${n(w - 2 * r)}a${n(r)} ${n(r)} 0 0 1 ${n(r)} ${n(r)}v${n(h - 2 * r)}a${n(r)} ${n(r)} 0 0 1 ${n(-r)} ${n(r)}h${n(-(w - 2 * r))}a${n(r)} ${n(r)} 0 0 1 ${n(-r)} ${n(-r)}v${n(-(h - 2 * r))}a${n(r)} ${n(r)} 0 0 1 ${n(r)} ${n(-r)}Z`

/** The family silhouette: a sheet with its top right corner folded. */
const sheet = (x: number, y: number, w: number, h: number, fold = 4.5, r = 2) =>
	`M${n(x + r)} ${n(y)}H${n(x + w - fold)}L${n(x + w)} ${n(y + fold)}V${n(y + h - r)}a${r} ${r} 0 0 1 ${-r} ${r}H${n(x + r)}a${r} ${r} 0 0 1 ${-r} ${-r}V${n(y + r)}a${r} ${r} 0 0 1 ${r} ${-r}Z`
const fold = (x: number, y: number, w: number, f = 4.5) =>
	`M${n(x + w - f)} ${n(y)}v${n(f - 1.5)}a1.5 1.5 0 0 0 1.5 1.5H${n(x + w)}`

const SHEET = sheet(5, 3, 14, 18)
const FOLD = fold(5, 3, 14)
const RING = c(12, 12, 9)

export const PATHS = {
	// Direction
	arrowRight: { s: 'M4.5 12h14.5M13.5 6.5 19 12l-5.5 5.5' },
	arrowLeft: { s: 'M19.5 12H5M10.5 6.5 5 12l5.5 5.5' },
	arrowUp: { s: 'M12 19.5V5M6.5 10.5 12 5l5.5 5.5' },
	arrowDown: { s: 'M12 4.5V19M6.5 13.5 12 19l5.5-5.5' },
	chevronDown: { s: 'm7 9.5 5 5 5-5' },
	chevronUp: { s: 'm7 14.5 5-5 5 5' },
	chevronRight: { s: 'm9.5 7 5 5-5 5' },
	chevronLeft: { s: 'm14.5 7-5 5 5 5' },
	external: {
		s: 'M13.5 4.5h6v6M19.5 4.5 11.5 12.5M17 14v3.5a2 2 0 0 1-2 2H6.5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2H10',
	},
	refresh: { s: 'M19.5 12a7.5 7.5 0 1 1-2.2-5.3l2.2 2.3M19.5 4.5V9H15' },
	back10: { s: 'M4.5 12a7.5 7.5 0 1 0 2.2-5.3L4.5 9M4.5 4.5V9H9M12 8.5V12l2.5 1.5' },
	undo: { s: 'M8.5 5 4.5 9l4 4M4.5 9h10a5 5 0 0 1 0 10H11' },
	flip: {
		s: 'M4.5 11a7.5 7.5 0 0 1 13.4-4.1l1.6 1.6M19.5 4v4.5H15M19.5 13a7.5 7.5 0 0 1-13.4 4.1l-1.6-1.6M4.5 20v-4.5H9',
	},
	swap: { s: 'm16 3.5 4 4-4 4M20 7.5H6M8 12.5l-4 4 4 4M4 16.5h14' },
	shuffle: {
		s: 'M3.5 7h2.6a4 4 0 0 1 3.3 1.8l5.2 7.4A4 4 0 0 0 17.9 18h2.6M3.5 18h2.6a4 4 0 0 0 3.3-1.8M14.6 8.8A4 4 0 0 1 17.9 7h2.6M17.5 4l3 3-3 3M17.5 15l3 3-3 3',
	},

	// Actions
	plus: { s: 'M12 5v14M5 12h14' },
	minus: { s: 'M5 12h14' },
	check: { s: 'm4.75 12.5 4.5 4.5 10-10.25' },
	close: { s: 'M6.5 6.5l11 11M17.5 6.5l-11 11' },
	trash: {
		s: 'M4 6.5h16M9.5 6.5V5A1.5 1.5 0 0 1 11 3.5h2A1.5 1.5 0 0 1 14.5 5v1.5M6 6.5l.8 12.1a2 2 0 0 0 2 1.9h6.4a2 2 0 0 0 2-1.9L18 6.5M10 10.5v6M14 10.5v6',
		f: 'M6 6.5l.8 12.1a2 2 0 0 0 2 1.9h6.4a2 2 0 0 0 2-1.9L18 6.5Z',
	},
	pencil: {
		s: 'M14.6 5.4a2 2 0 0 1 2.8 0l1.2 1.2a2 2 0 0 1 0 2.8L9 19l-4.5 1 1-4.5ZM13 7l4 4',
		f: 'M14.6 5.4a2 2 0 0 1 2.8 0l1.2 1.2a2 2 0 0 1 0 2.8L9 19l-4.5 1 1-4.5Z',
	},
	// Two sheets of the family: the copy is a sheet behind a sheet.
	copy: {
		s: 'M9.5 7.5H15l3.5 3.5v8a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-9.5a2 2 0 0 1 2-2ZM15 7.5V10a1 1 0 0 0 1 1h2.5M5.5 16V5a2 2 0 0 1 2-2h6',
		f: 'M9.5 7.5H15l3.5 3.5v8a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-9.5a2 2 0 0 1 2-2Z',
	},
	link: {
		s: 'M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1',
	},
	brokenLink: {
		s: 'M12.5 6.9l.5-.5a4 4 0 0 1 5.66 5.66l-1.06 1.06M11.5 17.1l-.5.5a4 4 0 0 1-5.66-5.66l1.06-1.06M4.5 4.5l2.25 2.25M19.5 19.5l-2.25-2.25',
	},
	share: {
		s: 'M8.5 10H7a2 2 0 0 0-2 2v6.5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V12a2 2 0 0 0-2-2h-1.5M12 14.5V3.5M8.5 7 12 3.5 15.5 7',
	},
	// The printer feeds out a sheet of the family.
	printer: {
		s: 'M7 9V4.5A1.5 1.5 0 0 1 8.5 3H14l3 3v3M7 17H5.5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H17M7 14h10v5.5a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 7 19.5Z',
		f: 'M5.5 9h13a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2Z',
	},
	upload: { s: 'M4.5 15v2.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V15M12 15.5V4.5M7.5 9 12 4.5 16.5 9' },
	cloudUp: {
		s: 'M8 18.5H7a4.5 4.5 0 0 1-.6-8.96 6 6 0 0 1 11.5 1.2A3.9 3.9 0 0 1 17 18.5h-1M12 20.5v-7.5M9 15.5l3-3 3 3',
	},
	search: { s: `${c(10.5, 10.5, 6.5)}M15.5 15.5 20 20`, f: c(10.5, 10.5, 6.5) },
	signOut: { s: 'M10 4H6.5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2H10M15.5 8l4 4-4 4M19.5 12H9.5' },
	menu: { s: 'M4 7h16M4 12h16M4 17h10' },
	more: { d: `${c(5.5, 12, 1.5)}${c(12, 12, 1.5)}${c(18.5, 12, 1.5)}` },
	grip: {
		d: `${c(9, 6, 1.25)}${c(15, 6, 1.25)}${c(9, 12, 1.25)}${c(15, 12, 1.25)}${c(9, 18, 1.25)}${c(15, 18, 1.25)}`,
	},
	settings: {
		s: `M4 7h8.5M17.5 7H20${c(15, 7, 2.5)}M4 17h2.5M11.5 17H20${c(9, 17, 2.5)}`,
	},
	fullscreen: {
		s: 'M4 9V6a2 2 0 0 1 2-2h3M15 4h3a2 2 0 0 1 2 2v3M20 15v3a2 2 0 0 1-2 2h-3M9 20H6a2 2 0 0 1-2-2v-3',
	},
	fullscreenExit: {
		s: 'M9 4v3a2 2 0 0 1-2 2H4M20 9h-3a2 2 0 0 1-2-2V4M15 20v-3a2 2 0 0 1 2-2h3M4 15h3a2 2 0 0 1 2 2v3',
	},
	eye: {
		s: `M2.75 12c2-4.2 5.1-6.25 9.25-6.25S19.25 7.8 21.25 12c-2 4.2-5.1 6.25-9.25 6.25S4.75 16.2 2.75 12Z${c(12, 12, 3)}`,
		f: 'M2.75 12c2-4.2 5.1-6.25 9.25-6.25S19.25 7.8 21.25 12c-2 4.2-5.1 6.25-9.25 6.25S4.75 16.2 2.75 12Z',
	},
	eyeOff: {
		s: 'M9.9 6c.68-.17 1.38-.25 2.1-.25 4.15 0 7.25 2.05 9.25 6.25a13 13 0 0 1-2.1 3.15M6.6 7.6C5 8.65 3.75 10.1 2.75 12c2 4.2 5.1 6.25 9.25 6.25 1.7 0 3.25-.35 4.6-1.05M9.9 9.9a3 3 0 0 0 4.2 4.2M4 4l16 16',
	},
	lock: {
		s: `${rr(5, 10.5, 14, 10)}M8 10.5V8a4 4 0 0 1 8 0v2.5M12 14.5v2`,
		f: rr(5, 10.5, 14, 10),
	},

	// Media
	play: { d: 'M7.5 5.6v12.8a1 1 0 0 0 1.5.86l10.6-6.4a1 1 0 0 0 0-1.72L9 4.74a1 1 0 0 0-1.5.86Z' },
	pause: { d: `${rr(6.5, 4.5, 4, 15, 1.5)}${rr(13.5, 4.5, 4, 15, 1.5)}` },
	watch: {
		s: RING,
		f: RING,
		d: 'M10 8.9v6.2a.7.7 0 0 0 1.06.6l4.9-3.1a.7.7 0 0 0 0-1.2l-4.9-3.1a.7.7 0 0 0-1.06.6Z',
	},
	volume: {
		s: 'M4 9.5v5a1 1 0 0 0 1 1h3l4.4 3.6a.6.6 0 0 0 1-.46V5.36a.6.6 0 0 0-1-.46L8 8.5H5a1 1 0 0 0-1 1ZM16.5 9a4.2 4.2 0 0 1 0 6M19 6.5a7.8 7.8 0 0 1 0 11',
	},
	volumeOff: {
		s: 'M4 9.5v5a1 1 0 0 0 1 1h3l4.4 3.6a.6.6 0 0 0 1-.46V5.36a.6.6 0 0 0-1-.46L8 8.5H5a1 1 0 0 0-1 1ZM16.5 9.5l4.5 5M21 9.5l-4.5 5',
	},
	// Video source, deliberately brand-neutral (no YouTube logo).
	video: {
		s: rr(3, 5, 18, 14, 3),
		f: rr(3, 5, 18, 14, 3),
		d: 'M10 9.4v5.2a.6.6 0 0 0 .92.5l4.1-2.6a.6.6 0 0 0 0-1l-4.1-2.6a.6.6 0 0 0-.92.5Z',
	},
	camera: {
		s: `M3.5 9a2 2 0 0 1 2-2h2.2l1.4-2.1a1 1 0 0 1 .83-.4h4.14a1 1 0 0 1 .83.4L16.3 7h2.2a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2Z${c(12, 13, 3.5)}`,
		f: 'M3.5 9a2 2 0 0 1 2-2h2.2l1.4-2.1a1 1 0 0 1 .83-.4h4.14a1 1 0 0 1 .83.4L16.3 7h2.2a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2Z',
	},
	photo: {
		s: `${rr(3.5, 5, 17, 14)}M3.5 16.5l4.4-4.4a1.5 1.5 0 0 1 2.1 0l5.5 5.4M14 15.5l1.4-1.4a1.5 1.5 0 0 1 2.1 0l3 3`,
		f: rr(3.5, 5, 17, 14),
		d: c(15.5, 9.5, 1.5),
	},
	// A paragraph: first line indented.
	text: { s: 'M8.5 6h11M4.5 10h15M4.5 14h15M4.5 18h9' },

	// Status
	checkCircle: { s: `${RING}M8 12.25l2.75 2.75L16 9.5`, f: RING },
	error: { s: `${RING}M9.25 9.25l5.5 5.5M14.75 9.25l-5.5 5.5`, f: RING },
	alert: { s: `${RING}M12 7.5V13`, f: RING, d: c(12, 16.25, 1.1) },
	info: { s: `${RING}M12 11v5.5`, f: RING, d: c(12, 7.75, 1.1) },
	help: {
		s: `${RING}M9.6 9.6a2.5 2.5 0 1 1 3.4 2.3c-.6.25-1 .85-1 1.5v.35`,
		f: RING,
		d: c(12, 16.5, 1.1),
	},
	warning: {
		s: 'M10.27 4.5a2 2 0 0 1 3.46 0l7.07 12.5a2 2 0 0 1-1.74 3H4.94a2 2 0 0 1-1.74-3ZM12 9.5v4',
		f: 'M10.27 4.5a2 2 0 0 1 3.46 0l7.07 12.5a2 2 0 0 1-1.74 3H4.94a2 2 0 0 1-1.74-3Z',
		d: c(12, 16.6, 1.1),
	},
	offline: {
		s: 'M2.5 9.5a13.5 13.5 0 0 1 19 0M5.5 12.75a9.2 9.2 0 0 1 13 0M8.6 15.9a4.8 4.8 0 0 1 6.8 0M4 4l16 16',
		d: c(12, 19, 1.25),
	},
	clock: { s: `${RING}M12 7.5V12l3 2`, f: RING },
	calendar: {
		s: `${rr(3.5, 5, 17, 15.5)}M3.5 10h17M8.5 3v4M15.5 3v4`,
		f: rr(3.5, 5, 17, 15.5),
		d: rr(7, 13, 3, 3, 0.75),
	},

	// People and places
	user: { s: `${c(12, 8.25, 3.75)}M5 20a7 7 0 0 1 14 0`, f: c(12, 8.25, 3.75) },
	users: {
		s: `${c(9, 8, 3.25)}M3.5 19.5a5.5 5.5 0 0 1 11 0M15.5 4.9a3.25 3.25 0 0 1 0 6.2M17.5 14.3a5.5 5.5 0 0 1 3 5.2`,
		f: c(9, 8, 3.25),
	},
	groups: {
		s: `${c(12, 7.5, 2.75)}M7 19a5 5 0 0 1 10 0${c(5.5, 10, 2)}M2.5 18a3.5 3.5 0 0 1 3.7-3.5${c(18.5, 10, 2)}M21.5 18a3.5 3.5 0 0 0-3.7-3.5`,
		f: c(12, 7.5, 2.75),
	},
	globe: {
		s: `${RING}M3 12h18M12 3c2.4 2.6 3.6 5.6 3.6 9S14.4 18.4 12 21c-2.4-2.6-3.6-5.6-3.6-9S9.6 5.6 12 3Z`,
		f: RING,
	},
	phone: { s: `${rr(6.5, 3, 11, 18, 2.5)}M10.5 18h3`, f: rr(6.5, 3, 11, 18, 2.5) },

	// Things on the desk
	sheet: { s: `${SHEET}${FOLD}M8.5 12h7M8.5 16h4.5`, f: SHEET },
	// A folder: a body with its tab, the label she files sheets under.
	folder: {
		s: 'M5 4.5h3.7c.5 0 .95.22 1.25.6l1.35 1.4H19a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6.5a2 2 0 0 1 2-2Z',
		f: 'M5 4.5h3.7c.5 0 .95.22 1.25.6l1.35 1.4H19a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6.5a2 2 0 0 1 2-2Z',
	},
	// Four tiles of the hub, one of them round: the tools are different shapes of one family.
	tools: {
		s: `${rr(4, 4, 6.5, 6.5, 1.75)}${c(16.75, 7.25, 3.5)}${rr(4, 13.5, 6.5, 6.5, 1.75)}${rr(13.5, 13.5, 6.5, 6.5, 1.75)}`,
		f: `${rr(4, 4, 6.5, 6.5, 1.75)}${c(16.75, 7.25, 3.5)}${rr(4, 13.5, 6.5, 6.5, 1.75)}${rr(13.5, 13.5, 6.5, 6.5, 1.75)}`,
	},
	list: {
		s: 'M9.5 7h10.5M9.5 12h10.5M9.5 17h10.5',
		d: `${c(5, 7, 1.25)}${c(5, 12, 1.25)}${c(5, 17, 1.25)}`,
	},
	chart: {
		s: `${rr(4.5, 12, 3.5, 8, 1.25)}${rr(10.25, 4, 3.5, 16, 1.25)}${rr(16, 8.5, 3.5, 11.5, 1.25)}`,
		f: `${rr(4.5, 12, 3.5, 8, 1.25)}${rr(10.25, 4, 3.5, 16, 1.25)}${rr(16, 8.5, 3.5, 11.5, 1.25)}`,
	},
	star: {
		s: 'M12 3.4l2.6 5.45 5.9.75-4.35 4.1 1.1 5.9L12 16.75 6.75 19.6l1.1-5.9L3.5 9.6l5.9-.75Z',
		f: 'M12 3.4l2.6 5.45 5.9.75-4.35 4.1 1.1 5.9L12 16.75 6.75 19.6l1.1-5.9L3.5 9.6l5.9-.75Z',
	},
	// AI drafting: the teacher's pen nib, with a glint. Not a sparkle star.
	sparkle: {
		s: 'M9.5 21V15.4M9.5 21 5.25 13.6 7 7.25h5l1.75 6.35ZM7 7.25V4.4A1.4 1.4 0 0 1 8.4 3h2.2A1.4 1.4 0 0 1 12 4.4v2.85M16 5V2.75M18.1 5.9l1.6-1.6M19 8h2.25',
		f: 'M9.5 21 5.25 13.6 7 7.25h5l1.75 6.35Z',
		d: c(9.5, 13.75, 1.25),
	},
	// Crossword: two words crossing on a grid.
	crossword: {
		s: 'M4.5 4.5h15v15h-10v-5h5v-5h-10ZM9.5 4.5v5M14.5 4.5v5M14.5 14.5h5M14.5 14.5v5',
		f: 'M4.5 4.5h15v15h-10v-5h5v-5h-10Z',
	},
	// Word search: a letter grid with one word circled.
	wordSearch: {
		s: 'M6.5 9.5h11a2.5 2.5 0 0 1 0 5h-11a2.5 2.5 0 0 1 0-5Z',
		f: 'M6.5 9.5h11a2.5 2.5 0 0 1 0 5h-11a2.5 2.5 0 0 1 0-5Z',
		d: `${c(6.5, 5.5, 1.15)}${c(12, 5.5, 1.15)}${c(17.5, 5.5, 1.15)}${c(6.5, 18.5, 1.15)}${c(12, 18.5, 1.15)}${c(17.5, 18.5, 1.15)}`,
	},
	// Match: two cards joined by a line.
	match: {
		s: `${rr(3, 4.5, 8, 6)}${rr(13, 13.5, 8, 6)}M11 7.5h4.5A1.5 1.5 0 0 1 17 9v4.5`,
		f: rr(3, 4.5, 8, 6),
	},
	// Fill the gap: a line of text with one blank to fill.
	gap: {
		s: `M3.5 9h5M15.5 9h5M5 16h14${rr(9.5, 6, 5, 6, 1.25)}`,
		f: rr(9.5, 6, 5, 6, 1.25),
	},

	// Tool marks: each carries one marker accent.
	// Exercises: a sheet with an answered box and lines.
	questionSheet: {
		s: `${SHEET}${FOLD}M13 11.5h3M8.5 16h7.5`,
		f: SHEET,
		a: rr(8, 10, 3, 3, 0.9),
	},
	// Vocabulary: a card in front of a card, the word marked.
	cards: {
		s: `${rr(4, 8, 13, 12)}M7.5 8V6a2 2 0 0 1 2-2h8.5a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-1M7 16.75h4.5`,
		f: rr(4, 8, 13, 12),
		a: rr(6.75, 11.25, 7, 2.5, 1.25),
	},
	// Levels: three sheets rising, the highest with its corner marked.
	levels: {
		s: `${rr(3.5, 13, 8, 8)}M7.5 13v-3a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-3.25M11.5 8V5a2 2 0 0 1 2-2H17l3.5 3.5V19a2 2 0 0 1-2 2h-3.25M17 3v2.5a1 1 0 0 0 1 1h2.5`,
		f: rr(3.5, 13, 8, 8),
		a: 'M17 3l3.5 3.5H18a1 1 0 0 1-1-1Z',
	},
	// Lesson plan: a sheet with a ribbon bookmark and timed steps.
	lessonPlan: {
		s: `${SHEET}${FOLD}M8.5 12.5h0M11 12.5h5M8.5 16.5h0M11 16.5h3.5`,
		f: SHEET,
		a: 'M8 3h3.5v5.3a.5.5 0 0 1-.8.4l-.95-.75-.95.75a.5.5 0 0 1-.8-.4Z',
	},
	// Name wheel: a wheel under its pointer peg.
	wheel: {
		s: `${c(12, 13, 8)}M12 7v14M5.07 9l13.86 8M18.93 9 5.07 17`,
		f: c(12, 13, 8),
		a: 'M9.6 2.5h4.8a.6.6 0 0 1 .5.93l-2.4 3.6a.6.6 0 0 1-1 0L9.1 3.43a.6.6 0 0 1 .5-.93Z',
		d: c(12, 13, 1.6),
	},
} satisfies Record<string, IconDef>

export type IconName = keyof typeof PATHS

/** Rendered stroke: 1.75px at 24, never thinner than 1.5px on screen (16px icons stay legible). */
export const strokeFor = (px: number) => Math.max(1.75, (1.5 * 24) / px)

/**
 * The logo mark: a lowercase t on the teal tile, its crossbar a swipe of the amber highlighter that
 * marks every "next step" in the product. Drawn on a 32 grid, legible down to a 16px favicon.
 * One source for ui/Logo.tsx, the landing pages (seo/icons.ts) and public/favicon.svg.
 */
export const LOGO = {
	marker: 'M8.4 14.6 23.6 12.4',
	t: 'M14.4 6.6v13.6a4.8 4.8 0 0 0 4.8 4.8h2.4',
}

/** The whole mark as standalone SVG markup (tile included). */
export const logoSvg = (attrs = '') =>
	`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" ${attrs}><rect width="32" height="32" rx="9" fill="#0f766e"/><path d="${LOGO.marker}" stroke="#f59e0b" stroke-width="5" stroke-linecap="round"/><path d="${LOGO.t}" fill="none" stroke="#fff" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`
